import * as angular from 'angular';
import * as _ from 'lodash';

import { IRequestService, IGroupSelectionService, IGroupService, ISeriesBuilder, IBindingResultError, SeriesMappingResult, ISeriesBuilderArguments } from '..';
import { Group, ErrorLevel, IGuid, ExtendedState, State, PopoverItemData } from '../../models';
import { GroupStateResults, ListGroupStateResults } from '../../widgets';
import { GroupStateConfiguration } from '../../widgets/groupStates/groupStates.configuration';
import { missingSeries, WideError } from '../../common';
import { bindingResultErrorToWideError, popoverItem, toDictionary } from '../../common/helpers';
import { StatePercentageDto } from '../../models/DTO/statePercentageDto';

export interface IGroupStatesWidgetService extends ISeriesBuilder {
    updateBoundSeries(configurationSeries: GroupStateConfiguration[]): angular.IPromise<GroupStateConfiguration[]>;
    getViewModels(series: GroupStateConfiguration[], groups: Group[]): angular.IPromise<any[]>;
    refresh(series: any[], beginDate: string, endDate: string): angular.IPromise<GroupStateResults[]>;
    map(series: any[], data: GroupStateResults[], states: State[]): GroupStateResults[];
}

export interface IGroupStateViewModel {
    name: string;
    guid: string;
    attributes: {
        'wt-popover-svg': boolean;
        template: string;
        item: string;
        compile: string;
    };
    percentages?: StatePercentageDto[];
    ranges?: ExtendedState[];
    callback?: () => void;
}

export class GroupStatesWidgetService implements IGroupStatesWidgetService {
    static serviceId = 'groupStatesWidgetService';
    static $inject = ['$q', '$state', 'requestService', 'groupSelectionService'];

    constructor(
        private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        private _requestService: IRequestService,
        private _groupSelectionService: IGroupSelectionService,
    ) {
    }

    getViewModels(series: GroupStateConfiguration[], groups: Group[]): angular.IPromise<IGroupStateViewModel[]> {
        const index = toDictionary(groups);
        const res = series.map(s => this.createViewModel(s, index[s.guid]));
        return this._$q.when(res);
    }

    updateBoundSeries(configurationSeries: GroupStateConfiguration[]): angular.IPromise<GroupStateConfiguration[]> {
        const defered = this._$q.defer<GroupStateConfiguration[]>();
        const bound = configurationSeries.filter(s => s.isBoundToGroup);
        const selectedGroup = this._groupSelectionService.selectedGroup();
        if (bound.length > 0 && selectedGroup) {
            defered.reject('not implemented');
        } else {
            defered.resolve(configurationSeries);
        }
        return defered.promise;
    }

    refresh(series: any[], beginDate: string, endDate: string): angular.IPromise<GroupStateResults[]> {
        return this._requestService
            .cachedPostAtV1('GroupResults', {
                beginDate: beginDate,
                endDate: endDate,
                guidList: series.map(d => d.guid)
            })
            .then((data: ListGroupStateResults) => this.map(series, data.groupStatesResults, data.states));
    }

    map(series: any[], data: GroupStateResults[], states: State[]): GroupStateResults[] {
        try {
            series.forEach(r => {
                const find = _.find(data, d => d.guid === r.guid);
                if (find) {
                    r.percentages = find.percentages;
                    r.states = find.states;
                }
            });
            const res = series.map(d => {
                return {
                    name: d.name,
                    guid: d.guid,
                    percentages: d.percentages,
                    attributes: d.attributes,
                    ranges: this.getStatesAsRange(d.states),
                    legends: this.getStatesAsLegend(states),
                    callback: () => this._$state.go('group.trend', { groupGuid: d.guid }),
                    states: d.states
                };
            });
            return res;
        } catch (e) {
            return [];
        }
    }

    private getStatesAsRange(states: ExtendedState[]): { name: string; guid: string; color: string }[] {
        return states?.map(dd => {
            return {
                name: dd.name,
                guid: dd.guid,
                color: dd.color,
                beginDateTime: new Date(dd.beginDateTime),
                endDateTime: new Date(dd.endDateTime)
                //iconUrl: this._stateService.getIcon(dd.iconUrl)
            };
        }) ?? [];
    }

    private getStatesAsLegend(states: State[]): { name: string; guid: string; color: string }[]
    {
        return states?.map(s => {
            return {
                name: s.name,
                guid: s.guid,
                color: s.markerColorString,
            };
        }) ?? [];
    }

    /**
     * Returns a list of view models from a list of series definition.
     * @param dic The dictionary of all groups in the model.
     * @param configuration The widget configuration.
     * @param map The guid-guid map of bound series.
     */
    buildSeriesFromViewModels(args: ISeriesBuilderArguments): angular.IPromise<SeriesMappingResult> {
        const { configuration, map, errors, groups } = args;
        const vms = configuration.series.map((c: GroupStateConfiguration) => {
            const guid = map && map[c.guid] ? map[c.guid] : c.guid;
            const group = groups[guid];
            return this.createViewModel(c, group);
        });

        let error;
        if (errors) {
            const bindMissing = configuration.series.filter(s => errors[s.guid])
                .map(s => errors[s.guid]);
            error = bindingResultErrorToWideError(bindMissing);
        }
        if (_.some(configuration.series, (s: IGuid) => !groups[s.guid])) {
            error = new WideError(ErrorLevel.Error, missingSeries);
        }
        return this._$q.when({
            error,
            series: vms,
            map
        });
    }

    private createViewModel(s: GroupStateConfiguration, d: Group): IGroupStateViewModel {
        const comment = s.hasComment
            ? ` - ${d.shortComment}`
            : '';
        const type = s.hasType ? ` (${d.type})` : '';
        return {
            guid: d.guid,
            name: d.name + comment + type,
            attributes: {
                'wt-popover-svg': true,
                template: 'groupPopoverTemplate.html',
                item: popoverItem(this.groupSub(d)),
                'compile': 'wt-popover-svg'
            }
        };
    }

    private groupSub(group: Group): PopoverItemData {
        return {
            type: group.type,
            longComment: group.longComment,
            shortComment: group.shortComment,
            parentUniqueName: group.parentUniqueName,
            name: group.name,
            uniqueName: group.uniqueName
        } as PopoverItemData;
    }
}